/**
 * Wayfinding menu config (A18) — every navigable destination with the roles
 * that may reach it. Used by the /find command palette (deterministic search).
 * Roles use the 4 base roles; MANAGER is mapped to HR by the caller.
 */
import type { MenuEntry } from '@payroll/shared';

/**
 * 🚨 หน้ากลุ่ม "สำนักงานใหญ่เท่านั้น" — ผู้จัดการสาขา (MANAGER) ห้ามเห็น
 *
 * ทุกจุดที่กรองเมนูฝั่งจอ map `MANAGER → HR` (navRole) เพื่อให้ผู้จัดการทำงาน
 * ประจำวันได้เหมือน HR แต่การ map นั้นพ่วงเมนูเงินเดือน/ค่าจ้าง/คอมมิชชั่น
 * มาด้วยทั้งชุด ⇒ หลังบ้านปิดด้วย requireHqOnly() แล้วกดเข้าไปเด้ง 403
 * = "ป้ายที่โกหก" (เห็นเมนูแต่ใช้ไม่ได้)
 *
 * ⚠️ ซ่อนเมนูอย่างเดียวไม่ใช่การปิดสิทธิ์ — App.tsx ต้องกันที่ router ด้วย
 *    (พิมพ์ URL ตรง ๆ ต้องเข้าไม่ได้) และ API ต้องมีด่านของตัวเองเสมอ
 */
export const HQ_ONLY_UI_ROUTES: readonly string[] = [
  '/owner/cycle',              // รอบเงินเดือน (รวม reconcile + close + payroll)
  '/owner/reconcile',
  '/owner/close-readiness',
  '/owner/payroll',            // ครอบ /owner/payroll/history ด้วย
  '/owner/payroll-analysis',
  '/owner/wage',               // ค่าจ้าง (หัก · เบี้ยเลี้ยง · คอม · ปลายปี)
  '/owner/deductions',
  '/owner/comp',               // โครงสร้างค่าตอบแทน
  '/owner/year-end',           // 50 ทวิ · ภงด.1ก
  '/owner/termination',        // คำนวณค่าชดเชยเลิกจ้าง
  '/owner/certificates',       // หนังสือรับรองเงินเดือน
  '/owner/notifications',      // ตั้งค่าแจ้งเตือนทั้งบริษัท
  '/owner/import-historical',  // นำเข้าเงินเดือน/ภาษี/ปกส. ย้อนหลัง
  '/owner/import-employees',   // นำเข้าพนักงาน = เขียนทับเลขบัญชีรับเงินได้
  '/owner/register-employees',
  '/owner/settings#import',    // แท็บที่รวมหน้านำเข้าทั้ง 3 (ย้ายออกจากเมนูข้าง 18 ก.ย. 2569)
  '/owner/hr-permissions',     // ตั้งสิทธิ์ผู้ใช้
  '/hr/commission',
  '/hr/commission-packages',
  '/hr/commission-runs',
  '/hr/allowances',
  '/hr/allowance-master',
  '/hr/payouts',               // รอบจ่ายเงินพิเศษ + ไฟล์โอนธนาคาร
  '/owner/blueprint',          // พิมพ์เขียวระบบ — โชว์เงินเพิ่ม/เงินหัก + สลิปงวดล่าสุด
  '/owner/approval-chain',     // สายอนุมัติ — ตั้งว่าใครอนุมัติเงินได้
  '/owner/setup',              // ฝังตั้งค่าแจ้งเตือนทั้งบริษัทไว้ข้างใน
  '/owner/company-docs',       // เอกสารบริษัท
  '/owner/company-doc-requests',
  '/owner/onboard',            // เปิดพนักงานใหม่เข้าระบบ (ตั้งค่าจ้าง)
  '/owner/onboarding',
  '/owner/onboarding-setup',
  // แท็บงานสำนักงานใหญ่ที่ฝังอยู่ในหน้าพนักงาน — หน้าหลัก /hr/people ยังเปิดให้ผู้จัดการ
  '/hr/people#onboard',
  '/hr/people#termination',
  '/owner/documents',          // เอกสารพนักงานทั้งบริษัท + ออกสัญญาจ้าง
  '/hr/jds',                   // คำบรรยายลักษณะงาน (JD)
  '/owner/contract-template',  // แก้ถ้อยคำสัญญาจ้าง
  '/owner/org',                // โครงสร้างองค์กร/แผนก
  '/owner/policies',           // ระเบียบบริษัท
];

/** เมนูสาขา: เปิดอ่านให้ผู้จัดการ (ต้องใช้เป็นตัวกรองในหน้าลงเวลา/ทะเบียน)
 *  — ปุ่มเพิ่ม/แก้/ปิดสาขาในหน้านั้นยังเป็นของสำนักงานใหญ่ (API กั้นไว้) */

/** เส้นนี้เป็นหน้ากลุ่มสำนักงานใหญ่ไหม (นับ path ลูก + hash/query ด้วย) */
export function isHqOnlyRoute(route: string): boolean {
  const clean = route.split('?')[0] ?? route;
  const base = clean.split('#')[0] ?? clean;
  return HQ_ONLY_UI_ROUTES.some((r) => {
    // entry ที่ระบุ hash (เช่น '/hr/people#onboard') = ปิดเฉพาะแท็บนั้น หน้าหลักยังเปิด
    if (r.includes('#')) return clean === r;
    return base === r || base.startsWith(r + '/');
  });
}

/** เมนูนี้ผู้ใช้ที่ถือ role จริงตัวนี้เห็นได้ไหม (ใช้ role จริง ไม่ใช่ navRole ที่ map แล้ว) */
export function menuAllowedForRole(route: string, actualRole: string): boolean {
  return !(actualRole === 'MANAGER' && isHqOnlyRoute(route));
}

export const MENU: MenuEntry[] = [
  // common / employee
  { label: 'เช็คอินด้วยรูป', route: '/checkin', roles: ['EMPLOYEE', 'SUPERVISOR', 'HR', 'OWNER'], keywords: ['checkin', 'ตอกบัตร', 'ลงเวลา'] },
  { label: 'ขอลา', route: '/leave', roles: ['EMPLOYEE', 'SUPERVISOR', 'HR', 'OWNER'], keywords: ['leave', 'ลาป่วย', 'ลากิจ', 'ลาพักร้อน'] },
  { label: 'ขอ OT', route: '/ot', roles: ['EMPLOYEE', 'SUPERVISOR', 'HR', 'OWNER'], keywords: ['overtime', 'โอที'] },
  { label: 'สิทธิ์การลา', route: '/quota', roles: ['EMPLOYEE', 'SUPERVISOR', 'HR', 'OWNER'], keywords: ['quota', 'โควต้า'] },
  { label: 'ยืนยันเวลาเลิกงาน', route: '/confirm-clockout', roles: ['EMPLOYEE', 'SUPERVISOR', 'HR', 'OWNER'], keywords: ['clockout', 'เลิกงาน'] },
  { label: 'ประวัติของฉัน', route: '/profile', roles: ['EMPLOYEE', 'SUPERVISOR', 'HR', 'OWNER'], keywords: ['profile'] },
  { label: 'สลิปเงินเดือน', route: '/slip', roles: ['EMPLOYEE', 'SUPERVISOR', 'HR', 'OWNER'], keywords: ['payslip', 'เงินเดือน', 'สลิป'] },
  { label: 'ร้องเรียน / แจ้งเหตุ', route: '/tickets', roles: ['EMPLOYEE', 'SUPERVISOR', 'HR', 'OWNER'], keywords: ['ticket', 'ร้องทุกข์'] },
  { label: 'ผู้ช่วย AI', route: '/ask', roles: ['EMPLOYEE', 'SUPERVISOR', 'HR', 'OWNER'], keywords: ['ai', 'ถาม', 'assistant', 'ผู้ช่วย'] },
  { label: 'อนุมัติคำขอ', route: '/approvals', roles: ['SUPERVISOR', 'HR', 'OWNER'], keywords: ['approve', 'อนุมัติ', 'emergency', 'ฉุกเฉิน', 'ลาฉุกเฉิน', 'pending', 'รออนุมัติ', 'แก้ข้อมูล', 'ticket', 'ร้องเรียน', 'ร้องทุกข์'] },
  // HR
  { label: 'แดชบอร์ด HR', route: '/hr', roles: ['HR', 'OWNER'], keywords: ['dashboard'] },
  { label: 'พนักงาน', route: '/hr/people', roles: ['HR', 'OWNER'], keywords: ['employees', 'รายชื่อ', 'พนักงาน', 'เพิ่มพนักงาน', 'ลาออก', 'เลิกจ้าง', 'ตามเก็บประวัติ', 'คำขอแก้ข้อมูล'] },
  { label: 'สาขา', route: '/hr/branches', roles: ['HR', 'OWNER'], keywords: ['branch', 'สาขา', 'multi-branch', 'หลายสาขา', 'geofence', 'พิกัดสาขา'] },
  { label: 'เวลาทำงาน', route: '/hr/time', roles: ['HR', 'OWNER'], keywords: ['time', 'เวลา', 'กะ', 'shift', 'เข้างาน', 'attendance', 'วันหยุด', 'holiday', 'วันลา', 'leave package', 'แพคเกจการลา', 'prorate', 'ยกยอด', 'สูตรเวลา', 'late grace', 'ot cap'] },
  { label: 'ตั้งสิทธิ์การลา (โควต้า)', route: '/hr/quotas', roles: ['HR', 'OWNER'], keywords: ['quota'] },
  { label: 'ตั้งค่าพื้นที่ออฟฟิศ (GPS)', route: '/hr/office-location', roles: ['HR', 'OWNER'], keywords: ['gps', 'geofence', 'พิกัด', 'ออฟฟิศ', 'สำนักงาน', 'ตำแหน่ง', 'เช็คอิน', 'checkin', 'wfh', 'บังคับพิกัด', 'พื้นที่'] },
  // เงินเพิ่ม → ย้ายไป Wage hub (search ผ่าน "ค่าจ้าง")
  // Owner — รอบเงินเดือน hub (รวม reconcile + close-readiness + payroll เป็น tabs)
  { label: 'รอบเงินเดือน', route: '/owner/cycle', roles: ['HR', 'OWNER'], keywords: ['payroll', 'รันเงินเดือน', 'reconcile', 'สรุปเวลา', 'close', 'readiness', 'ปิดงวด', 'ตรวจก่อนปิด', 'ทำเงินเดือน', 'รอบ'] },
  { label: 'ค่าจ้าง', route: '/owner/wage', roles: ['HR', 'OWNER'], keywords: ['wage', 'ค่าจ้าง', 'analysis', 'variance', 'วิเคราะห์', 'deduction', 'หัก', 'กยศ', 'allowance', 'เบี้ยขยัน', 'เงินเพิ่ม', 'commission', 'คอม', 'year-end', 'ปลายปี', '50ทวิ', 'ภงด'] },
  { label: 'คอมมิชชั่น', route: '/hr/commission', roles: ['HR', 'OWNER'], keywords: ['commission', 'คอมมิชชั่น', 'คอม', 'คำนวณคอม', 'commission run'] },
  // สรุปปลายปี → ย้ายไป Wage hub
  // people (HR/Owner)
  // เพิ่มพนักงาน / ลาออก / ตามเก็บประวัติ → รวมเข้า People hub แล้ว (search ผ่าน label 'พนักงาน')
  { label: 'กะการทำงาน', route: '/hr/shifts', roles: ['HR', 'OWNER'], keywords: ['shift', 'กะ', 'เวร'] },
  // owner reports / settings
  // คำขอแก้ข้อมูล → ใน People hub tab "คำขอแก้ข้อมูล"
  // ประวัติการใช้งาน + สำรอง Drive → tabs ใน /owner/settings
  { label: 'ตั้งค่าองค์กร', route: '/owner/settings', roles: ['OWNER'], keywords: ['settings', 'ตั้งค่า', 'owner', 'audit', 'log', 'ประวัติการใช้งาน', 'drive', 'backup', 'สำรอง', 'บริษัท', 'องค์กร'] },
  // ย้ายออกจากเมนูข้าง 18 ก.ย. 2569 — ค้นคำว่า "นำเข้า" ยังต้องเจอ
  { label: 'นำเข้าข้อมูลครั้งแรก (พนักงาน/ย้อนหลัง)', route: '/owner/settings#import', roles: ['OWNER', 'HR'], keywords: ['import', 'นำเข้า', 'excel', 'csv', 'พนักงาน', 'เลขบัตร', 'ย้อนหลัง', 'เงินเดือนย้อนหลัง', 'ประวัติลา', 'ครั้งแรก'] },
  { label: 'เริ่มต้นด้วยเทมเพลต', route: '/owner/templates', roles: ['OWNER'], keywords: ['template', 'เทมเพลต', 'preset', 'industry', 'อุตสาหกรรม', 'restaurant', 'office', 'factory', 'retail', 'clinic'] },
  { label: 'ขั้นตอนการทำงาน', route: '/owner/guide', roles: ['HR', 'OWNER'], keywords: ['guide', 'flow', 'คู่มือ', 'ขั้นตอน', 'workflow', 'วิธีใช้'] },
  { label: 'ตั้งค่าผู้ช่วย AI', route: '/owner/ai', roles: ['OWNER'], keywords: ['byok', 'api key', 'ai', 'ผู้ช่วย'] },
  { label: 'เอกสารบริษัท', route: '/owner/company-docs', roles: ['HR', 'OWNER'], keywords: ['เอกสาร', 'บริษัท', 'หนังสือรับรองบริษัท', 'ภพ.20', 'ตราประทับ', 'คำขอเอกสาร', 'สำเนาถูกต้อง'] },
  { label: 'คำบรรยายลักษณะงาน (JD)', route: '/hr/jds', roles: ['HR', 'OWNER'], keywords: ['jd', 'คำบรรยายลักษณะงาน', 'หน้าที่', 'ตำแหน่ง', 'คุณสมบัติ', 'ตัวชี้วัด'] },
  { label: 'แก้ถ้อยคำสัญญา', route: '/owner/contract-template', roles: ['HR', 'OWNER'], keywords: ['สัญญาจ้าง', 'เทมเพลต', 'ถ้อยคำ', 'contract'] },
  { label: 'เอกสารบริษัท', route: '/company-docs', roles: ['EMPLOYEE', 'SUPERVISOR', 'HR', 'OWNER'], keywords: ['ขอเอกสาร', 'สำเนาถูกต้อง', 'ยื่นคำขอเอกสาร'] },
];
